import { Controller } from "@hotwired/stimulus"

// Signs someone out after SESSION_IDLE_TIMEOUT with no interaction at all, and
// warns them a minute before.
//
// The server is the authority: Authentication expires the session row on its
// own whether or not this script ever runs. This exists for the two things the
// server can't see. One is activity that never becomes a request (scrolling a
// results page, typing into the addresses form), which it reports through
// /account/keepalive. The other is the warning.
//
// Time is read off the wall clock on every tick rather than counted down with
// setTimeout, because background tabs throttle timers and a laptop lid stops
// them outright. Opening the lid at minute forty signs out on the first tick.
//
// Shared across tabs through localStorage, so reading in one tab keeps the
// others alive and signing out in one signs out all of them.
const LAST_ACTIVITY_KEY = "dmd:lastActivity"
const SIGNED_OUT_KEY = "dmd:signedOut"
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "scroll", "wheel", "touchstart", "mousemove"]
const STORE_EVERY_MS = 5_000
const PING_EVERY_MS = 30_000

export default class extends Controller {
  static targets = ["warning", "countdown", "stay", "form"]
  static values = { timeout: Number, warning: Number, keepaliveUrl: String, signinUrl: String }

  connect() {
    this.lastActivity = Date.now()
    this.lastStored = 0
    this.lastPing = Date.now() // this page load was a request
    this.signingOut = false
    this.store(LAST_ACTIVITY_KEY, this.lastActivity)

    this.onActivity = this.activity.bind(this)
    this.onStorage = this.storage.bind(this)
    this.onVisible = this.tick.bind(this)
    ACTIVITY_EVENTS.forEach((type) =>
      window.addEventListener(type, this.onActivity, { passive: true, capture: true }))
    window.addEventListener("storage", this.onStorage)
    document.addEventListener("visibilitychange", this.onVisible)
    this.interval = setInterval(() => this.tick(), 1_000)
  }

  disconnect() {
    ACTIVITY_EVENTS.forEach((type) =>
      window.removeEventListener(type, this.onActivity, { capture: true }))
    window.removeEventListener("storage", this.onStorage)
    document.removeEventListener("visibilitychange", this.onVisible)
    clearInterval(this.interval)
    clearTimeout(this.trailingPing)
  }

  activity() {
    if (this.signingOut) return

    const now = Date.now()
    this.lastActivity = now
    if (now - this.lastStored >= STORE_EVERY_MS) {
      this.lastStored = now
      this.store(LAST_ACTIVITY_KEY, now)
    }
    this.schedulePing()
    if (!this.warningTarget.hidden) this.tick()
  }

  stay() {
    this.activity()
    this.store(LAST_ACTIVITY_KEY, this.lastActivity)
    this.ping()
  }

  // At most one ping per PING_EVERY_MS, but always one after the last burst
  // of activity. That trailing ping is what keeps the server's last_seen_at
  // at or after the last thing the person did, so the server can never end
  // the session before this script would.
  schedulePing() {
    const wait = this.lastPing + PING_EVERY_MS - Date.now()
    if (wait <= 0) return this.ping()
    if (this.trailingPing) return

    this.trailingPing = setTimeout(() => {
      this.trailingPing = null
      if (this.lastActivity > this.lastPing) this.ping()
    }, wait)
  }

  async ping() {
    clearTimeout(this.trailingPing)
    this.trailingPing = null
    this.lastPing = Date.now()

    try {
      // redirect: "manual" so an expired session's redirect to /signin isn't
      // followed here, which would spend the flash explaining why on a page
      // nobody sees.
      const response = await fetch(this.keepaliveUrlValue, {
        credentials: "same-origin",
        cache: "no-store",
        redirect: "manual",
        headers: { "X-Requested-With": "XMLHttpRequest" }
      })
      if (!response.ok) this.goToSignIn()
    } catch {
      // Offline or a blip. The server keeps counting, and the next ping or
      // the timer settles it.
    }
  }

  tick() {
    if (this.signingOut) return

    const remaining = this.timeoutValue * 1_000 - (Date.now() - this.newestActivity())
    if (remaining <= 0) return this.signOut()

    if (remaining <= this.warningValue * 1_000) {
      this.countdownTarget.textContent = Math.ceil(remaining / 1_000)
      if (this.warningTarget.hidden) {
        this.warningTarget.hidden = false
        this.stayTarget.focus({ preventScroll: true })
      }
    } else if (!this.warningTarget.hidden) {
      this.warningTarget.hidden = true
    }
  }

  newestActivity() {
    return Math.max(this.lastActivity, Number(this.read(LAST_ACTIVITY_KEY)) || 0)
  }

  signOut() {
    this.signingOut = true
    this.store(SIGNED_OUT_KEY, Date.now())
    this.formTarget.requestSubmit()
  }

  storage(event) {
    if (event.key === SIGNED_OUT_KEY) this.goToSignIn()
    else if (event.key === LAST_ACTIVITY_KEY) this.tick()
  }

  goToSignIn() {
    if (this.signingOut) return
    this.signingOut = true
    window.location.assign(this.signinUrlValue)
  }

  // Private windows, blocked site data and Safari's storage quotas all throw.
  // Without storage each tab keeps its own clock, which is still correct for
  // that tab.
  store(key, value) {
    try { localStorage.setItem(key, String(value)) } catch {}
  }

  read(key) {
    try { return localStorage.getItem(key) } catch { return null }
  }
}
